import { Box } from '@mui/material';
import _ from 'lodash';
import moment, { Moment } from 'moment-timezone';
import { Views } from 'react-big-calendar';
import invariant from 'tiny-invariant';

import { useAppConfig } from '../../fetching/api/appConfig';
import { Shift, ShiftBlock } from '../../fetching/sharedSchemas/misc';
import { VisitForList } from '../../fetching/sharedSchemas/visits';
import { getShiftBlockInstanceId, getShiftBlockInstanceLabel } from '../../utils/shiftBlocks';
import { QhpChip, QhpChipProps } from '../displayFields/chips';
import { AxleCalendar, AxleEvent } from './AxleCalendar';

export type SchedulingEvent = AxleEvent & {
    eventType:
        | 'shiftBlock'
        | 'confirmedVisit'
        | 'tentativeVisit'
        | 'unavailable'
        | 'misc'
        | 'tentativeUnscheduledVisit';
};

export type QhpForSchedulingCalendar = {
    // TODO:(drew) convert to camel case when we no longer need to support QhpSimple
    uuid: string;
    name: string;
    first_name: string;
    last_name: string;
    timezone: string;
    logo_url: string | null;
};

export const SchedulingCalendar = ({
    qhps,
    shifts,
    confirmedVisits,
    tentativeVisits,
    shiftBlocks,
    selectedDate,
    setVisitToEdit,
}: {
    qhps: QhpForSchedulingCalendar[];
    shifts: Shift[];
    confirmedVisits: VisitForList[];
    tentativeVisits: SchedulingEvent[];
    shiftBlocks: ShiftBlock[];
    selectedDate: Moment;
    setVisitToEdit: (id: string) => void;
}) => {
    const appConfig = useAppConfig();
    const getStyleForEvent = (
        event: SchedulingEvent,
    ): { style: Record<string, string | number> } => {
        const defaultStyle = {
            style: {
                borderRadius: '5px',
                color: 'white',
                border: '0px',
                fontSize: '11px',
            },
        };
        if (event.eventType === 'confirmedVisit') {
            return {
                style: {
                    ...defaultStyle.style,
                    backgroundColor: '#3D6B6B',
                    opacity: 1,
                },
            };
        } else if (event.eventType === 'unavailable') {
            return {
                style: {
                    ...defaultStyle.style,
                    backgroundColor: '#D9D9D9',
                    opacity: 0.5,
                },
            };
        } else if (event.eventType === 'shiftBlock') {
            return {
                style: {
                    ...defaultStyle.style,
                    backgroundImage:
                        'repeating-linear-gradient(45deg,' +
                        '#666 10px,' +
                        '#666 12px,' +
                        '#111 12px,' +
                        '#112 21px)',
                },
            };
        } else {
            return {
                style: {
                    ...defaultStyle.style,
                    backgroundColor: '#66C1BF',
                    opacity: 1,
                },
            };
        }
    };

    const createBackgroundEvents = (): SchedulingEvent[] => {
        const startOfDay = selectedDate.clone().startOf('day');
        const endOfDay = selectedDate.clone().endOf('day');

        const backgroundEvents: SchedulingEvent[] = [];
        // Iterate over each assignee
        qhps.forEach((qhp) => {
            // Filter shifts for the current assignee
            const assigneeShifts = shifts.filter((shift) => shift.professional.uuid === qhp.uuid);

            const combinedShifts: Shift[] = [];
            assigneeShifts.forEach((assigneeShift) => {
                combinedShifts.forEach((existingShift) => {
                    if (
                        assigneeShift.start_datetime.isBefore(existingShift.end_datetime) &&
                        assigneeShift.end_datetime.isAfter(existingShift.start_datetime)
                    ) {
                        // Merge overlapping shifts into a larger time period
                        existingShift.start_datetime = moment.min(
                            assigneeShift.start_datetime,
                            existingShift.start_datetime,
                        );
                        existingShift.end_datetime = moment.max(
                            assigneeShift.end_datetime,
                            existingShift.end_datetime,
                        );
                    }
                });

                combinedShifts.push({ ...assigneeShift }); // Create a copy of the shift
            });

            // Sort shifts by start time
            const sortedShifts = combinedShifts.sort(
                (a, b) => a.start_datetime.valueOf() - b.start_datetime.valueOf(),
            );

            let backgroundEventsCount = 0;
            // If QHP has no shifts for the day, add a block for the entire day
            if (sortedShifts.length === 0) {
                backgroundEvents.push({
                    uuid: qhp.uuid + '-' + backgroundEventsCount.toString(),
                    title: '',
                    start: startOfDay.toDate(),
                    end: endOfDay.toDate(),
                    resourceId: qhp.uuid,
                    eventType: 'unavailable',
                });
                return;
            }

            // Add a block before the first shift
            if (sortedShifts.length > 0 && sortedShifts[0].start_datetime.isAfter(startOfDay)) {
                backgroundEvents.push({
                    uuid: qhp.uuid + '-' + backgroundEventsCount.toString(),
                    title: '',
                    start: startOfDay.toDate(),
                    end: sortedShifts[0].start_datetime.toDate(),
                    resourceId: qhp.uuid,
                    eventType: 'unavailable',
                });
                backgroundEventsCount += 1;
            }

            // Add blocks between shifts
            for (let i = 0; i < sortedShifts.length - 1; i++) {
                const currentShift = sortedShifts[i];
                const nextShift = sortedShifts[i + 1];
                if (currentShift.end_datetime.isBefore(nextShift.start_datetime)) {
                    backgroundEvents.push({
                        uuid: qhp.uuid + '-' + backgroundEventsCount.toString(),
                        title: '',
                        start: currentShift.end_datetime.toDate(),
                        end: nextShift.start_datetime.toDate(),
                        resourceId: qhp.uuid,
                        eventType: 'unavailable',
                    });
                    backgroundEventsCount += 1;
                }
            }

            // Add a block after the last shift
            if (
                sortedShifts.length > 0 &&
                sortedShifts[sortedShifts.length - 1].end_datetime.isBefore(endOfDay)
            ) {
                backgroundEvents.push({
                    uuid: qhp.uuid + '-' + backgroundEventsCount.toString(),
                    title: '',
                    start: sortedShifts[sortedShifts.length - 1].end_datetime.toDate(),
                    end: endOfDay.toDate(),
                    resourceId: qhp.uuid,
                    eventType: 'unavailable',
                });
                // noinspection JSUnusedAssignment
                backgroundEventsCount += 1;
            }
        });

        return backgroundEvents;
    };
    const backgroundEvents = createBackgroundEvents();

    const confirmedVisitPositionEvents: SchedulingEvent[] = confirmedVisits.reduce(
        (events: SchedulingEvent[], confirmedVisit) => {
            confirmedVisit.visit_positions.forEach((visitPosition) => {
                if (visitPosition.qhp === null || confirmedVisit.datetime === null) {
                    return;
                }
                events.push({
                    uuid: confirmedVisit.uuid,
                    title: `${confirmedVisit.patient.name}-${_.flatten(
                        confirmedVisit.visit_positions.map((position) =>
                            position.visit_services.map(
                                (visitService) => visitService.service.name,
                            ),
                        ),
                    ).join(', ')}`,
                    start: confirmedVisit.datetime.toDate(),
                    end: confirmedVisit.datetime
                        .clone()
                        .add(visitPosition.duration_minutes, 'minutes')
                        .toDate(),
                    resourceId: visitPosition.qhp.uuid,
                    eventType: 'confirmedVisit',
                });
            });
            return events;
        },
        [],
    );

    const shiftBlockEvents = shiftBlocks.map((shiftBlock) => {
        const scheduleEvent: SchedulingEvent = {
            uuid: getShiftBlockInstanceId(shiftBlock),
            title: getShiftBlockInstanceLabel(shiftBlock),
            start: shiftBlock.start_datetime.toDate(),
            end: shiftBlock.end_datetime.toDate(),
            resourceId: shiftBlock.qhp.uuid,
            eventType: 'shiftBlock',
        } as SchedulingEvent;
        return scheduleEvent;
    });

    const components = {
        toolbar: () => null,
        resourceHeader: ({ resource: qhp }: { resource: QhpChipProps['qhp'] }) => (
            <Box
                sx={{
                    padding: 0.5,
                    height: '100%',
                }}
            >
                <QhpChip qhp={qhp} showLink={true} wrap={false} />
            </Box>
        ),
    };

    const onSelectEventHandler = (event: SchedulingEvent) => {
        if (event.eventType === 'confirmedVisit' && appConfig.visit.edit.dialog) {
            const confirmedVisitToEdit = confirmedVisits.find(
                (confirmedVisit) => confirmedVisit.uuid === event.uuid,
            );
            invariant(confirmedVisitToEdit !== undefined, 'confirmedVisitToEdit is undefined');
            setVisitToEdit(confirmedVisitToEdit.uuid);
        }
    };

    return (
        <AxleCalendar
            // Columns
            resources={qhps}
            resourceTitleAccessor={'name'}
            resourceIdAccessor={'uuid'}
            // Events
            backgroundEvents={backgroundEvents}
            events={confirmedVisitPositionEvents.concat(tentativeVisits).concat(shiftBlockEvents)}
            // Date.
            date={selectedDate.toDate()}
            defaultView={Views.DAY}
            views={[Views.DAY]}
            min={new Date(0, 0, 0, 0, 0, 0, 0)}
            max={new Date(0, 0, 0, 23, 59, 0, 0)}
            step={30}
            // Event Handlers
            selectable={true}
            onSelectEvent={onSelectEventHandler}
            // commenting for hover now, this doesn't seem to be a prop in the calendar component
            // hover={false}
            // Styling
            components={components}
            onSelecting={() => false} // Disable click-and-drag selection
            getStyleForEvent={getStyleForEvent}
            // Event Selection
            noEventsText='To view schedule information, please ensure that the clinicians you would like to view have shifts in the Clinician Schedules page on this day.'
        />
    );
};
